import { useEffect, useImperativeHandle, useMemo, useState, forwardRef } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import FilestoreBrowser from "./FilestoreBrowser";
import { parseFilestoreFile, FilestoreFile } from "../api/filestore";
import { useCreateFireHistory } from "../api/fireHistory";
import { useTimezone } from "../contexts/TimezoneContext";

const collectGeojsonPropertyFields = (data: any): string[] => {
  if (!data) return [];
  if (!data.features || !Array.isArray(data.features)) return [];

  const keys = new Set<string>();
  data.features.forEach((feature: any) => {
    if (feature?.properties && typeof feature.properties === "object") {
      Object.keys(feature.properties).forEach((key) => keys.add(key));
    }
  });
  return Array.from(keys).sort((a, b) => a.localeCompare(b));
};

const collectGeojsonFieldSamples = (data: any, field: string, limit: number = 3): string[] => {
  if (!data || !field) return [];
  if (!data.features || !Array.isArray(data.features)) return [];

  const samples: string[] = [];
  const seen = new Set<string>();

  for (const feature of data.features) {
    if (samples.length >= limit) break;
    const value = feature?.properties?.[field];
    if (value !== undefined && value !== null) {
      const str = String(value);
      if (!seen.has(str)) {
        seen.add(str);
        samples.push(str);
      }
    }
  }
  return samples;
};

export interface AddFireHistoryModalRef {
  open: () => void;
}

const AddFireHistoryModal = forwardRef<AddFireHistoryModalRef>((_, ref) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { selectedTimezone } = useTimezone();
  const createFireHistory = useCreateFireHistory(projectId ?? "");

  const [open, setOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    open: () => setOpen(true),
  }));
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filestoreBrowserOpen, setFilestoreBrowserOpen] = useState(false);
  const [fileName, setFileName] = useState("");
  const [fileError, setFileError] = useState("");
  const [uploadedGeojson, setUploadedGeojson] = useState<any>(null);

  // Date configuration
  const [dateMode, setDateMode] = useState<"fixed" | "field">("fixed");
  const [fixedDate, setFixedDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [dateField, setDateField] = useState("");
  const [dateFormat, setDateFormat] = useState("YYYY-MM-DD");

  // Name configuration
  const [nameMode, setNameMode] = useState<"fixed" | "field">("fixed");
  const [fixedName, setFixedName] = useState("");
  const [nameField, setNameField] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});

  const propertyFields = useMemo(() => collectGeojsonPropertyFields(uploadedGeojson), [uploadedGeojson]);
  const dateFieldSamples = useMemo(() => {
    if (dateMode !== "field" || !dateField) return [];
    return collectGeojsonFieldSamples(uploadedGeojson, dateField, 3);
  }, [uploadedGeojson, dateField, dateMode]);
  const nameFieldSamples = useMemo(() => {
    if (nameMode !== "field" || !nameField) return [];
    return collectGeojsonFieldSamples(uploadedGeojson, nameField, 3);
  }, [uploadedGeojson, nameField, nameMode]);

  // Auto-select first field when switching to field mode
  useEffect(() => {
    if (dateMode === "field" && propertyFields.length > 0 && !dateField) {
      setDateField(propertyFields[0]);
    }
  }, [dateMode, propertyFields, dateField]);

  useEffect(() => {
    if (nameMode === "field" && propertyFields.length > 0 && !nameField) {
      setNameField(propertyFields[0]);
    }
  }, [nameMode, propertyFields, nameField]);

  const reset = () => {
    setOpen(false);
    setIsFileLoading(false);
    setIsSubmitting(false);
    setFilestoreBrowserOpen(false);
    setFileName("");
    setFileError("");
    setUploadedGeojson(null);
    setDateMode("fixed");
    setFixedDate(dayjs());
    setDateField("");
    setDateFormat("YYYY-MM-DD");
    setNameMode("fixed");
    setFixedName("");
    setNameField("");
    setErrors({});
  };

  const handleFilestoreFileSelected = async (filestoreFile: FilestoreFile) => {
    setFileError("");
    setIsFileLoading(true);
    try {
      const parsed = await parseFilestoreFile(filestoreFile.id);

      if (!parsed || typeof parsed !== "object") {
        throw new Error("Invalid file format: not a valid GeoJSON object");
      }

      const geojsonData = parsed.features ? parsed : parsed.data;

      if (!geojsonData || !Array.isArray(geojsonData.features)) {
        throw new Error("Invalid file format: missing features array");
      }

      if (geojsonData.features.length === 0) {
        throw new Error("File contains no features");
      }

      const hasValidGeometry = geojsonData.features.some((f: any) => f?.geometry);
      if (!hasValidGeometry) {
        throw new Error("File contains no valid geometries");
      }

      setUploadedGeojson(geojsonData);
      setFileName(filestoreFile.original_filename);
      setDateMode("fixed");
      setDateField("");
      setNameMode("fixed");
      setNameField("");
      setFileError("");
    } catch (err: any) {
      console.error(err);
      setFileError(err?.message || "Failed to load file from filestore");
      setUploadedGeojson(null);
    } finally {
      setIsFileLoading(false);
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!uploadedGeojson) {
      newErrors.file = "No file loaded";
    }

    if (dateMode === "fixed") {
      if (!fixedDate || !fixedDate.isValid()) {
        newErrors.fixedDate = "Select a valid date";
      }
    } else {
      if (propertyFields.length === 0) {
        newErrors.dateField = "No properties found in the uploaded file";
      } else if (!dateField) {
        newErrors.dateField = "Select a property field for the date";
      }
      if (!dateFormat) {
        newErrors.dateFormat = "Enter the date format (e.g., YYYY-MM-DD)";
      }
    }

    if (nameMode === "field") {
      if (propertyFields.length === 0) {
        newErrors.nameField = "No properties found in the uploaded file";
      } else if (!nameField) {
        newErrors.nameField = "Select a property field for the fire name";
      }
    }

    return newErrors;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const dateConfig = {
        mode: dateMode,
        ...(dateMode === "fixed"
          ? { value: fixedDate?.tz(selectedTimezone, true).format("YYYY-MM-DD") }
          : { field: dateField, format: dateFormat }),
      };

      const nameConfig = {
        mode: nameMode,
        ...(nameMode === "fixed" && fixedName ? { value: fixedName } : {}),
        ...(nameMode === "field" ? { field: nameField } : {}),
      };

      await createFireHistory.mutateAsync({
        geojson: uploadedGeojson,
        dateConfig,
        nameConfig,
      });

      reset();
    } catch (error: any) {
      console.error("Failed to create fire history:", error);
      setErrors({ submit: error?.message || "Failed to create fire history records" });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={!isSubmitting ? reset : undefined}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: 2,
          p: 3,
          minWidth: 400,
          maxWidth: 500,
          maxHeight: "calc(var(--vh100) * 0.8)",
          overflow: "auto",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Add Fire History
        </Typography>

        {/* File Upload Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
            Upload a GeoJSON, KML, or Shapefile (.zip) with fire perimeters
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setFilestoreBrowserOpen(true)}
            disabled={isFileLoading || isSubmitting}
          >
            Browse My Files
          </Button>
          {fileName && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Selected: {fileName}
            </Typography>
          )}

          {fileError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {fileError}
            </Alert>
          )}

          {isFileLoading && (
            <Box sx={{ display: "flex", alignItems: "center", mt: 1, gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading file...
              </Typography>
            </Box>
          )}

          {errors.file && (
            <FormHelperText error>{errors.file}</FormHelperText>
          )}
        </Box>

        {uploadedGeojson && (
          <>
            <Divider sx={{ mb: 2 }} />

            {/* Date Configuration */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Date Configuration
            </Typography>

            <RadioGroup
              value={dateMode}
              onChange={(e) => setDateMode(e.target.value as "fixed" | "field")}
              sx={{ mb: 2 }}
            >
              <FormControlLabel value="fixed" control={<Radio />} label="Use a fixed date for all fires" />
              <FormControlLabel
                value="field"
                control={<Radio />}
                label="Read date from file property"
                disabled={propertyFields.length === 0}
              />
            </RadioGroup>

            {dateMode === "fixed" ? (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Fire Date"
                  value={fixedDate}
                  onChange={setFixedDate}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      error: !!errors.fixedDate,
                      helperText: errors.fixedDate,
                      sx: { mb: 2 },
                    },
                  }}
                />
              </LocalizationProvider>
            ) : (
              <>
                <FormControl fullWidth size="small" sx={{ mb: 2 }} error={!!errors.dateField}>
                  <InputLabel>Date Property Field</InputLabel>
                  <Select
                    value={dateField}
                    onChange={(e) => setDateField(e.target.value)}
                    label="Date Property Field"
                  >
                    {propertyFields.map((field) => (
                      <MenuItem key={field} value={field}>
                        {field}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.dateField && <FormHelperText>{errors.dateField}</FormHelperText>}
                </FormControl>

                <TextField
                  label="Date Format"
                  fullWidth
                  size="small"
                  value={dateFormat}
                  onChange={(e) => setDateFormat(e.target.value)}
                  error={!!errors.dateFormat}
                  helperText={
                    errors.dateFormat ||
                    "Examples: YYYY-MM-DD, DD/MM/YYYY, MM/DD/YYYY"
                  }
                  sx={{ mb: 1 }}
                />

                {dateFieldSamples.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                    Sample values: {dateFieldSamples.join(", ")}
                  </Typography>
                )}
              </>
            )}

            <Divider sx={{ mb: 2 }} />

            {/* Fire Name Configuration */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Fire Name Configuration
            </Typography>

            <RadioGroup
              value={nameMode}
              onChange={(e) => setNameMode(e.target.value as "fixed" | "field")}
              sx={{ mb: 2 }}
            >
              <FormControlLabel value="fixed" control={<Radio />} label="Use a fixed name" />
              <FormControlLabel
                value="field"
                control={<Radio />}
                label="Read name from file property"
                disabled={propertyFields.length === 0}
              />
            </RadioGroup>

            {nameMode === "fixed" ? (
              <TextField
                label="Fire Name"
                fullWidth
                size="small"
                value={fixedName}
                onChange={(e) => setFixedName(e.target.value)}
                placeholder="Leave empty to use Fire 1, Fire 2, etc."
                sx={{ mb: 2 }}
              />
            ) : (
              <>
                <FormControl fullWidth size="small" sx={{ mb: 2 }} error={!!errors.nameField}>
                  <InputLabel>Name Property Field</InputLabel>
                  <Select
                    value={nameField}
                    onChange={(e) => setNameField(e.target.value)}
                    label="Name Property Field"
                  >
                    {propertyFields.map((field) => (
                      <MenuItem key={field} value={field}>
                        {field}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.nameField && <FormHelperText>{errors.nameField}</FormHelperText>}
                </FormControl>

                {nameFieldSamples.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                    Sample values: {nameFieldSamples.join(", ")}
                  </Typography>
                )}
              </>
            )}
          </>
        )}

        {errors.submit && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            {errors.submit}
          </Alert>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
          <Button onClick={reset} variant="outlined" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!uploadedGeojson || isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={20} /> : "Add Fire History"}
          </Button>
        </Box>

        <FilestoreBrowser
          open={filestoreBrowserOpen}
          onClose={() => setFilestoreBrowserOpen(false)}
          onSelectFile={handleFilestoreFileSelected}
          acceptedFileTypes=".zip,.kml,.geojson,.json"
        />
      </Box>
    </Modal>
  );
});

AddFireHistoryModal.displayName = "AddFireHistoryModal";

export default AddFireHistoryModal;
